// File: client/contest-client/src/pages/ParticipantEntriesPage.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchParticipantEntries, type ParticipantEntry } from '../api/participantEntries';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Box,
    CircularProgress,
    TableSortLabel,
    Select,
    MenuItem,
    useTheme,
} from '@mui/material';
import { SwipeHint } from "../utils/SwipeHint";
import Medal from "../utils/Medal";
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';

const ParticipantEntriesPage: React.FC = () => {
    const { participantId } = useParams<{ participantId: string }>();
    const [entries, setEntries] = useState<ParticipantEntry[]>([]);
    const [expandedRow, setExpandedRow] = useState<number | null>(null);
    const [sortBy, setSortBy] = useState<keyof ParticipantEntry | null>(null);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [selectedContestId, setSelectedContestId] = useState<string>('');
    const theme = useTheme();

    useEffect(() => {
        if (participantId) {
            fetchParticipantEntries(participantId)
                .then((rows) => setEntries(rows))
                .catch((error) => console.error(error));
        }
    }, [participantId]);

    const handleSort = (column: keyof ParticipantEntry) => {
        const isAsc = sortBy === column && sortOrder === 'asc';
        setSortOrder(isAsc ? 'desc' : 'asc');
        setSortBy(column);
    };

    const sortedEntries = useMemo(() => {
        if (!sortBy) return entries;
        return [...entries].sort((a, b) => {
            const aValue = a[sortBy] ?? '';
            const bValue = b[sortBy] ?? '';
            if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
    }, [entries, sortBy, sortOrder]);

    const contestOptions = useMemo(() => {
        const map = new Map<number, { name: string; count: number }>();
        for (const e of sortedEntries) {
            const id = e.contest_id;
            const name = e.contest_name || 'Неизвестно';
            const cur = map.get(id) ?? { name, count: 0 };
            cur.count += 1;
            map.set(id, cur);
        }
        return Array.from(map.entries())
            .map(([id, v]) => ({ contest_id: id, contest_name: v.name, count: v.count }))
            .sort((a, b) => a.contest_name.localeCompare(b.contest_name));
    }, [sortedEntries]);

    const filteredEntries = useMemo(() => {
        if (!selectedContestId) return sortedEntries;
        const id = Number(selectedContestId);
        if (Number.isNaN(id)) return sortedEntries;
        return sortedEntries.filter((e) => e.contest_id === id);
    }, [sortedEntries, selectedContestId]);

    const filterSummary = useMemo(() => {
        const total = filteredEntries.length;
        const podiums = filteredEntries.reduce((acc, e) => {
            const r = typeof e.ranking === 'number' ? e.ranking : null;
            return r !== null && r >= 1 && r <= 3 ? acc + 1 : acc;
        }, 0);
        const groups = new Set(filteredEntries.map((e) => e.grouping).filter(Boolean)).size;

        const dates = filteredEntries
            .map((e) => e.created_at)
            .filter(Boolean)
            .map((d) => new Date(d as string))
            .filter((d) => !Number.isNaN(d.getTime()))
            .sort((a, b) => a.getTime() - b.getTime());
        const from = dates.length ? dates[0].toLocaleDateString('ru-RU') : '—';
        const to = dates.length ? dates[dates.length - 1].toLocaleDateString('ru-RU') : '—';

        return { total, podiums, groups, from, to };
    }, [filteredEntries]);

    const handleExportCsv = () => {
        if (!filteredEntries.length) return;

        const headers: (keyof ParticipantEntry | 'created_at_local')[] = [
            'contest_id',
            'contest_name',
            'result',
            'result_unit',
            'grouping',
            'ranking',
            'total_participants',
            'created_at',
            'created_at_local',
            'participant_data',
            'participant_name',
            'participant_id',
            'evidence_url',
            'profile_picture',
        ];

        const esc = (val: unknown) => {
            if (val === null || val === undefined) return '""';
            const s = String(val).replace(/"/g, '""').replace(/\r?\n/g, ' ');
            return `"${s}"`;
        };

        const rows = filteredEntries.map(e => {
            const createdLocal = e.created_at
                ? new Date(e.created_at).toLocaleString('ru-RU')
                : '';
            return headers.map(h =>
                h === 'created_at_local' ? esc(createdLocal) : esc(e[h as keyof ParticipantEntry])
            ).join(',');
        });

        const csv = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const fileName = `participant_${participantId || 'unknown'}_entries.csv`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.setAttribute('download', fileName);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return !entries.length ? (
        <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress />
        </Box>
    ) : (
        <div>
            <Box mb={2} display="flex" gap={2} flexWrap="wrap">
                <Select
                    value={selectedContestId}
                    onChange={(e) => setSelectedContestId(String(e.target.value))}
                    displayEmpty
                    sx={{
                        width: 240,
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                    }}
                >
                    <MenuItem value="">Все зарубы ({entries.length})</MenuItem>
                    {contestOptions.map((c) => (
                        <MenuItem key={c.contest_id} value={String(c.contest_id)}>
                            {c.contest_name} ({c.count})
                        </MenuItem>
                    ))}
                </Select>
                <Button variant="outlined" onClick={handleExportCsv}>
                    Export CSV
                </Button>
                <Button
                    variant="text"
                    onClick={() => setSelectedContestId('')}
                    disabled={!selectedContestId}
                    sx={{ textTransform: 'none' }}
                >
                    Сбросить фильтр
                </Button>
            </Box>

            <Box mb={2} display="flex" gap={2} flexWrap="wrap" sx={{ opacity: 0.9 }}>
                <Box><b>Записей:</b> {filterSummary.total}</Box>
                <Box><b>Подиумов (1–3):</b> {filterSummary.podiums}</Box>
                <Box><b>Групп:</b> {filterSummary.groups}</Box>
                <Box><b>Период:</b> {filterSummary.from} – {filterSummary.to}</Box>
            </Box>
            <TableContainer
                component={Paper}
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                }}
            >
                <Box display="flex" justifyContent="center" mb={1}>
                    <SwipeHint />
                </Box>
                <Table>
                    <TableHead>
                        <TableRow>
                            {[
                                { label: 'Заруба', field: 'contest_name' },
                                { label: 'Результат', field: 'result' },
                                { label: 'Группа', field: 'grouping' },
                                { label: 'Рейтинг', field: 'ranking' },
                                { label: 'Всего участников', field: '' },
                                { label: 'Дата', field: 'created_at' },
                                { label: 'Заметки участника', field: '' },
                            ].map((column, index) => (
                                <TableCell
                                    key={index}
                                    sx={{
                                        fontWeight: 'bold',
                                        backgroundColor: theme.palette.background.default,
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    {column.field ? (
                                        <TableSortLabel
                                            active={sortBy === column.field}
                                            direction={sortBy === column.field ? sortOrder : 'asc'}
                                            onClick={() => handleSort(column.field as keyof ParticipantEntry)}
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    ) : (
                                        column.label
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredEntries.map((entry, index) => (
                            <TableRow
                                key={index}
                                sx={{
                                    backgroundColor:
                                        index % 2 === 0
                                            ? theme.palette.background.default
                                            : theme.palette.background.paper,
                                    color: theme.palette.text.primary,
                                }}
                            >
                                <TableCell>
                                    <Box display="flex" flexDirection="column" alignItems="flex-start">
                                        <Box display="flex" alignItems="center" mb={0.5}>
                                            {entry.ranking === 1 && (
                                                <EmojiEventsIcon
                                                    fontSize="small"
                                                    sx={{ color: theme.palette.warning.main, mr: 1 }}
                                                />
                                            )}
                                            <Link
                                                to={`/contest/${entry.contest_id}`}
                                                style={{
                                                    textDecoration: 'none',
                                                    color: theme.palette.primary.main,
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                {entry.contest_name || 'Неизвестно'}
                                            </Link>
                                        </Box>
                                        {entry.evidence_url && (
                                            <Box mt={0.5} display="flex" alignItems="center">
                                                <Link
                                                    to={entry.evidence_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        color: theme.palette.primary.main,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <OndemandVideoIcon fontSize="small" sx={{ mr: 1 }} />
                                                    Видео
                                                </Link>
                                            </Box>
                                        )}
                                    </Box>
                                </TableCell>
                                <TableCell>{entry.result ?? '—'}</TableCell>
                                <TableCell>
                                    <Link
                                        to={`/contest/${entry.contest_id}/?tab=1&group=${encodeURIComponent(
                                            entry.grouping ?? ''
                                        )}`}
                                        style={{
                                            textDecoration: 'none',
                                            color: theme.palette.primary.main,
                                        }}
                                    >
                                        {entry.grouping ?? '-'}
                                    </Link>
                                </TableCell>
                                <TableCell>
                                    <Medal ranking={entry.ranking} />
                                </TableCell>
                                <TableCell>{entry.total_participants ?? '—'}</TableCell>
                                <TableCell>
                                    {entry.created_at
                                        ? new Date(entry.created_at).toLocaleString('ru-RU')
                                        : '—'}
                                </TableCell>
                                <TableCell style={{ maxWidth: '150px', wordWrap: 'break-word' }}>
                                    {expandedRow === index ? (
                                        <>
                                            {entry.participant_data ?? 'Нет данных'}
                                            <Button
                                                onClick={() => setExpandedRow(null)}
                                                sx={{ textTransform: 'none', fontSize: '12px', ml: 1 }}
                                            >
                                                Скрыть
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            {(entry.participant_data
                                                ? (entry.participant_data.length > 50
                                                    ? `${entry.participant_data.slice(0, 50)}...`
                                                    : entry.participant_data)
                                                : 'Нет данных')}
                                            {entry.participant_data && entry.participant_data.length > 50 && (
                                                <Button
                                                    onClick={() => setExpandedRow(index)}
                                                    sx={{ textTransform: 'none', fontSize: '12px', ml: 1 }}
                                                >
                                                    Читать далее
                                                </Button>
                                            )}
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default ParticipantEntriesPage;
